import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Save, Loader2, AlertTriangle, Trash2 } from 'lucide-react';
import { PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/components/ui/Toaster';
import { Tooltip } from '@/components/ui/Tooltip';
import { useDeleteTenant, usePatchTenant, useTenantPlan, type PatchTenantBody, type PlanOverrides } from '@/api/tenants';
import { usePlans } from '@/api/plans';
import { useTenant } from './context';
import { OperatorPicker } from '@/components/ui/OperatorPicker';
import { SIZE_ORDER, tierLabel } from '@/lib/tier';
import type { Tier } from '@/data/types';

export function TenantSettings() {
  const { tenant, workspaces } = useTenant();
  const patch = usePatchTenant();
  const plan = useTenantPlan(tenant.slug);
  const allPlans = usePlans();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Local form state mirrors the tenant; we send only the diff on save.
  const [name, setName] = useState(tenant.name);
  const [ownerName, setOwnerName] = useState(tenant.ownerName);
  const [ownerEmail, setOwnerEmail] = useState(tenant.ownerEmail);
  const [supportOwner, setSupportOwner] = useState(tenant.supportOwner ?? '');
  const [tier, setTier] = useState<Tier>(tenant.tier);
  const [notes, setNotes] = useState(tenant.notes ?? '');

  // Adjustments (MC-610) — '' = plan default. Feature tri-state:
  // 'default' | 'on' | 'off'.
  const [ovInstances, setOvInstances] = useState('');
  const [ovSize, setOvSize] = useState('');
  const [ovUsers, setOvUsers] = useState('');
  const [ovPlugins, setOvPlugins] = useState('');
  const [ovFeatures, setOvFeatures] = useState<Record<string, 'default' | 'on' | 'off'>>({});

  function loadOverrides(ov: PlanOverrides | null) {
    setOvInstances(ov?.max_instances === undefined ? '' : String(ov.max_instances ?? '∞'));
    setOvSize(ov?.max_size === undefined ? '' : (ov.max_size ?? '__any__'));
    setOvUsers(ov?.entitlement?.limits?.max_users === undefined ? '' : String(ov.entitlement.limits.max_users ?? '∞'));
    setOvPlugins(ov?.entitlement?.limits?.max_plugins === undefined ? '' : String(ov.entitlement.limits.max_plugins ?? '∞'));
    const feats: Record<string, 'default' | 'on' | 'off'> = {};
    for (const [k, v] of Object.entries(ov?.entitlement?.features ?? {})) feats[k] = v ? 'on' : 'off';
    setOvFeatures(feats);
  }

  // If the tenant data changes (e.g. another tab refreshes), pick it up.
  useEffect(() => {
    setName(tenant.name);
    setOwnerName(tenant.ownerName);
    setOwnerEmail(tenant.ownerEmail);
    setSupportOwner(tenant.supportOwner ?? '');
    setTier(tenant.tier);
    setNotes(tenant.notes ?? '');
  }, [tenant]);

  // Overrides arrive with the plan payload, separately from the tenant.
  const storedOverrides = plan.data?.overrides ?? null;
  useEffect(() => {
    loadOverrides(storedOverrides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(storedOverrides)]);

  function buildOverrides(): PlanOverrides | null {
    const num = (s: string) => (s === '∞' ? null : Number(s));
    const ov: PlanOverrides = {};
    if (ovInstances !== '' && (ovInstances === '∞' || Number.isFinite(Number(ovInstances)))) {
      ov.max_instances = num(ovInstances);
    }
    if (ovSize !== '') ov.max_size = ovSize === '__any__' ? null : ovSize;
    const limits: NonNullable<NonNullable<PlanOverrides['entitlement']>['limits']> = {};
    if (ovUsers !== '' && (ovUsers === '∞' || Number.isFinite(Number(ovUsers)))) limits.max_users = num(ovUsers);
    if (ovPlugins !== '' && (ovPlugins === '∞' || Number.isFinite(Number(ovPlugins)))) limits.max_plugins = num(ovPlugins);
    const features: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(ovFeatures)) if (v !== 'default') features[k] = v === 'on';
    const ent: NonNullable<PlanOverrides['entitlement']> = {};
    if (Object.keys(limits).length) ent.limits = limits;
    if (Object.keys(features).length) ent.features = features;
    if (Object.keys(ent).length) ov.entitlement = ent;
    return Object.keys(ov).length ? ov : null;
  }

  const overridesDirty = JSON.stringify(buildOverrides()) !== JSON.stringify(storedOverrides);

  const dirty =
    name !== tenant.name ||
    ownerName !== tenant.ownerName ||
    ownerEmail !== tenant.ownerEmail ||
    (supportOwner || '') !== (tenant.supportOwner ?? '') ||
    tier !== tenant.tier ||
    (notes || '') !== (tenant.notes ?? '') ||
    overridesDirty;

  // Provision-time hint only — the backend enforces. Warns whenever the
  // selected plan (with any pending adjustment) allows fewer instances
  // than the tenant already runs.
  const selectedPlan = allPlans.data?.find(p => p.slug === tier);
  const selectedName = selectedPlan?.name ?? tierLabel(tier);
  const pendingOv = buildOverrides();
  const selectedMax =
    pendingOv?.max_instances !== undefined
      ? pendingOv.max_instances
      : selectedPlan?.provision.max_instances ?? null;
  const instanceCount = plan.data?.usage.instances;
  const overLimit =
    selectedMax !== null && instanceCount !== undefined && instanceCount > selectedMax;

  async function save() {
    if (!dirty) return;
    const body: PatchTenantBody = {};
    if (name !== tenant.name) body.name = name.trim();
    if (ownerName !== tenant.ownerName) body.owner_name = ownerName.trim();
    if (ownerEmail !== tenant.ownerEmail) body.owner_email = ownerEmail.trim();
    if ((supportOwner || '') !== (tenant.supportOwner ?? '')) body.operator_owner = supportOwner.trim();
    if (tier !== tenant.tier) body.tier = tier;
    if ((notes || '') !== (tenant.notes ?? '')) body.notes = notes.trim();
    if (overridesDirty) body.plan_overrides = buildOverrides();

    try {
      await patch.mutateAsync({ tenantId: tenant.slug, ...body });
      toast.success('Saved', 'Customer details updated.');
    } catch (e: any) {
      toast.danger('Save failed', e?.message ?? 'Unknown error');
    }
  }

  function reset() {
    setName(tenant.name);
    setOwnerName(tenant.ownerName);
    setOwnerEmail(tenant.ownerEmail);
    setSupportOwner(tenant.supportOwner ?? '');
    setTier(tenant.tier);
    setNotes(tenant.notes ?? '');
    loadOverrides(storedOverrides);
  }

  return (
    <PageBody>
      <div className="space-y-4 max-w-3xl">
        <Card>
          <CardHeader
            title="Identity"
            description="Customer-facing details. Slug + ID are immutable — changing them would break links."
          />
          <CardBody className="space-y-3 text-sm">
            <Row label="Customer name">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
              />
            </Row>
            <Row label="Slug" readOnly>
              <span className="font-mono text-xs text-fg-muted">{tenant.slug}</span>
            </Row>
            <Row label="Customer ID" readOnly>
              <span className="font-mono text-xs text-fg-muted">{tenant.id}</span>
            </Row>
            <Row label="Owner name">
              <input
                value={ownerName}
                onChange={e => setOwnerName(e.target.value)}
                className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
              />
            </Row>
            <Row label="Owner email">
              <input
                value={ownerEmail}
                onChange={e => setOwnerEmail(e.target.value)}
                type="email"
                className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
              />
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Support owner"
            description="The NousViz operator responsible for this customer. Picks from the Staff list — invite them on the Staff page first if needed."
          />
          <CardBody>
            <OperatorPicker value={supportOwner} onChange={setSupportOwner} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Plan"
            description="The tier drives provision-time limits here and the licence each workspace fetches. Changes reach a workspace on its next daily licence check."
          />
          <CardBody className="space-y-3 text-sm">
            <Row label="Tier">
              <select
                value={tier}
                onChange={e => setTier(e.target.value as Tier)}
                className="h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
              >
                {(allPlans.data?.map(p => ({ slug: p.slug, name: p.name }))
                  ?? plan.data?.plans
                  ?? [{ slug: tier, name: selectedName }]
                ).map(p => (
                  <option key={p.slug} value={p.slug}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Row>
            <Row label="Instances" readOnly>
              {plan.data ? (
                <span>
                  {plan.data.usage.instances} of{' '}
                  {plan.data.limits.max_instances ?? 'unlimited'} used
                </span>
              ) : (
                <span className="text-fg-subtle">{plan.isError ? '—' : 'Loading…'}</span>
              )}
            </Row>
            <Row label="Max server size" readOnly>
              {plan.data ? (
                <span className="font-mono text-xs">
                  {plan.data.limits.max_size ?? 'unlimited'}
                </span>
              ) : (
                <span className="text-fg-subtle">{plan.isError ? '—' : 'Loading…'}</span>
              )}
            </Row>
            <Row label="Licence" readOnly>
              {plan.data ? (
                <span>
                  Users {plan.data.entitlement.limits.max_users ?? 'unlimited'} · Plugins{' '}
                  {plan.data.entitlement.limits.max_plugins ?? 'unlimited'} ·{' '}
                  {Object.entries(plan.data.entitlement.features)
                    .map(([k, on]) => `${FEATURE_LABEL[k] ?? k} ${on ? '✓' : '—'}`)
                    .join(' · ')}
                </span>
              ) : (
                <span className="text-fg-subtle">
                  {plan.isError ? 'Couldn’t load licence details.' : 'Loading…'}
                </span>
              )}
            </Row>
            {/* Adjustments (MC-610): per-customer deviations from the plan.
                Empty field = plan default. */}
            <div className="pt-1">
              <div className="text-fg-muted text-xs uppercase tracking-wider font-semibold mb-1">
                Adjustments
                {storedOverrides && (
                  <span className="ml-1.5 normal-case tracking-normal font-normal text-accent">custom values active</span>
                )}
              </div>
              <p className="text-xs text-fg-subtle mb-2 leading-snug">
                Per-customer deviations from the {selectedName} plan. Empty = plan
                default; type ∞ for unlimited. Licence changes reach workspaces on
                their next daily check.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <AdjustField
                  label="Max instances"
                  hint="Provision-time cap for this customer only — the panel refuses new instances beyond it. Existing instances are never deleted."
                  value={ovInstances}
                  onChange={setOvInstances}
                  placeholder={selectedPlan ? String(selectedPlan.provision.max_instances ?? 'unlimited') : '…'}
                />
                <div>
                  <AdjustLabel
                    overridden={ovSize !== ''}
                    hint="Largest droplet size offered when provisioning for this customer."
                  >Max server size</AdjustLabel>
                  <select
                    value={ovSize}
                    onChange={e => setOvSize(e.target.value)}
                    className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
                  >
                    <option value="">
                      Plan default ({selectedPlan ? selectedPlan.provision.max_size ?? 'any' : '…'})
                    </option>
                    <option value="__any__">Any size</option>
                    {SIZE_ORDER.filter(s => s !== 'shared' && s !== 'byo').map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <AdjustField
                  label="Max users (licence)"
                  hint="Enforced inside the customer's NousViz: invites blocked at the limit, nobody deleted. Reaches each workspace on its next daily licence check."
                  value={ovUsers}
                  onChange={setOvUsers}
                  placeholder={selectedPlan ? String(selectedPlan.entitlement.limits.max_users ?? 'unlimited') : '…'}
                />
                <AdjustField
                  label="Max plugins (licence)"
                  hint="Licence cap on installed plugins, enforced in-product with the same grandfathering promise."
                  value={ovPlugins}
                  onChange={setOvPlugins}
                  placeholder={selectedPlan ? String(selectedPlan.entitlement.limits.max_plugins ?? 'unlimited') : '…'}
                />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                {Object.keys({ mcp: 0, embedding: 0, sso: 0, ...selectedPlan?.entitlement.features, ...ovFeatures }).map(f => (
                  <div key={f}>
                    <AdjustLabel overridden={(ovFeatures[f] ?? 'default') !== 'default'}>
                      {FEATURE_LABEL[f] ?? f}
                    </AdjustLabel>
                    <select
                      value={ovFeatures[f] ?? 'default'}
                      onChange={e => setOvFeatures({ ...ovFeatures, [f]: e.target.value as 'default' | 'on' | 'off' })}
                      className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
                    >
                      <option value="default">
                        Plan default ({selectedPlan ? (selectedPlan.entitlement.features[f] ? 'on' : 'off') : '…'})
                      </option>
                      <option value="on">On</option>
                      <option value="off">Off</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {overLimit && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-warn/40 bg-[color:var(--warn-soft)] text-xs leading-snug">
                <AlertTriangle size={13} className="text-warn shrink-0 mt-0.5" />
                <span>
                  {tenant.name} runs {instanceCount} instances; the {selectedName} plan
                  allows {selectedMax}. Existing workspaces are kept — new provisioning is
                  blocked until the count is within plan.
                </span>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Notes"
            description="Internal notes — only visible to operators."
          />
          <CardBody>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="Add any context that's useful to know about this customer…"
              className="w-full px-3 py-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm leading-snug resize-none"
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Danger zone"
            description="Deleting a customer removes their record from this panel — servers are never destroyed implicitly."
          />
          <CardBody>
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-fg-muted leading-snug">
                {workspaces.length > 0 ? (
                  <>
                    {tenant.name} has {workspaces.length} instance{workspaces.length === 1 ? '' : 's'} —
                    destroy each on the <Link to={`/tenants/${tenant.slug}/workspaces`} className="underline">Instances tab</Link> before
                    the customer can be deleted.
                  </>
                ) : (
                  <>No instances — this customer can be deleted. You'll be asked to type the slug and give a reason.</>
                )}
              </p>
              <Button
                variant="danger"
                size="sm"
                iconLeft={<Trash2 size={13} />}
                disabled={workspaces.length > 0}
                title={workspaces.length > 0 ? 'Destroy all instances first.' : undefined}
                onClick={() => setDeleteOpen(true)}
              >
                Delete customer
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Save bar — sticky to bottom of the page area while there are unsaved changes */}
        {dirty && (
          <div className="sticky bottom-4 flex items-center justify-between gap-3 px-4 py-2.5 rounded-md border border-accent/40 bg-[color:var(--accent-soft)]/80 backdrop-blur-md text-sm">
            <span className="text-fg inline-flex items-center gap-1.5">
              <AlertTriangle size={13} className="text-accent" /> You have unsaved changes.
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={reset} disabled={patch.isPending}>
                Discard
              </Button>
              <Button variant="primary" size="sm" iconLeft={patch.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} onClick={save} disabled={patch.isPending}>
                {patch.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <DeleteTenantModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        tenantSlug={tenant.slug}
        tenantName={tenant.name}
      />
    </PageBody>
  );
}

function DeleteTenantModal({ open, onClose, tenantSlug, tenantName }: {
  open: boolean;
  onClose: () => void;
  tenantSlug: string;
  tenantName: string;
}) {
  const nav = useNavigate();
  const del = useDeleteTenant();
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const canSubmit = confirm === tenantSlug && reason.trim().length > 0 && !del.isPending;

  function reset() { setConfirm(''); setReason(''); }

  async function submit() {
    if (!canSubmit) return;
    try {
      await del.mutateAsync({ tenantId: tenantSlug, confirm, reason: reason.trim() });
      toast.warn(`${tenantName} deleted`, 'Customer record removed.');
      reset();
      onClose();
      nav('/tenants');
    } catch (e: any) {
      toast.danger('Delete failed', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!del.isPending) { reset(); onClose(); } }}
      size="md"
      title={
        <span className="inline-flex items-center gap-2 text-[color:var(--danger)]">
          <Trash2 size={16} /> Delete {tenantName}
        </span>
      }
      description="This removes the customer record, its plan assignment, and any per-customer adjustments from the panel. It is irreversible. Audit history is kept."
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={del.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submit} disabled={!canSubmit}>
            {del.isPending
              ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Deleting…</>
              : 'Delete customer'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5 text-sm">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Type <code className="font-mono normal-case">{tenantSlug}</code> to confirm
          </label>
          <input
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoFocus
            disabled={del.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Reason (recorded in the audit log)
          </label>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. churned — contract ended 31 Jul"
            disabled={del.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          />
        </div>
      </div>
    </Modal>
  );
}

const FEATURE_LABEL: Record<string, string> = {
  mcp: 'MCP',
  embedding: 'Embedding',
  sso: 'SSO',
};

function AdjustLabel({ overridden, hint, children }: {
  overridden: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  const text = hint ? (
    <Tooltip content={<span>{hint}</span>} maxWidth={280}>
      <span className="cursor-help underline decoration-dotted decoration-fg-subtle underline-offset-2">
        {children}
      </span>
    </Tooltip>
  ) : children;
  return (
    <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
      {text}
      {overridden && <span className="normal-case tracking-normal font-normal ml-1 text-accent">(custom)</span>}
    </label>
  );
}

function AdjustField({ label, hint, value, onChange, placeholder }: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <AdjustLabel overridden={value !== ''} hint={hint}>{label}</AdjustLabel>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
      />
    </div>
  );
}

function Row({
  label,
  children,
  readOnly,
}: {
  label: string;
  children: React.ReactNode;
  readOnly?: boolean;
}) {
  return (
    <div className="grid grid-cols-[180px_1fr] items-center gap-3 border-b border-border/60 last:border-0 pb-3 last:pb-0">
      <span className="text-fg-muted text-xs uppercase tracking-wider font-semibold">
        {label}
        {readOnly && <span className="text-fg-subtle font-normal lowercase tracking-normal ml-1">(read-only)</span>}
      </span>
      <div>{children}</div>
    </div>
  );
}
